import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Unit } from '@edtech-ui/core/models/manual-course/manual-couse-payload';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { QuillModule } from 'ngx-quill';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-admin-course-creation-modules',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon, ReactiveFormsModule, QuillModule],
  templateUrl: './admin-course-creation-modules.component.html',
  styleUrl: './admin-course-creation-modules.component.scss'
})
export class AdminCourseCreationModulesComponent {
  classroomId: string = '';
  savedUnits: Unit[] = [];
  addUnitVisible = false;
  showAddAnotherBtn = false;
  showSavedUnits = true;
  editingIndex: number | null = null;
  unitForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    private courseService: CourseManagementService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.parent?.paramMap.subscribe((params) => {
      this.classroomId = params.get('classroomId') ?? '';
    });
    const payload = this.courseService.getPayload();
    if (payload.units && payload.units.length) {
      this.savedUnits = JSON.parse(JSON.stringify(payload.units));
    }

    this.initForm();
  }
  markAllControlsTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllControlsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  initForm(): void {
    this.unitForm = this.fb.group({
      unitTitle: ['', Validators.required],
      unitHours: [
        '',
        [
          Validators.required, Validators.min(1), Validators.pattern(/^[0-9]+$/)
        ]
      ],
      topics: this.fb.array([])
    });
  }

  get topics(): FormArray {
    return this.unitForm.get('topics') as FormArray;
  }

  addTopic(): void {
    const topicGroup = this.fb.group({
      title: ['', Validators.required],
      showSubtopics: [true],
      subtopics: this.fb.array([]),
      description: ['']

    });
    this.topics.push(topicGroup);
  }

  deleteTopic(index: number): void {
    this.topics.removeAt(index);
  }
  deleteUnit(index: number): void {
    this.savedUnits.splice(index, 1);
    this.courseService.updatePayload({ units: this.savedUnits });
    this.toastr.info('Unit deleted successfully');
  }

  toggleSubtopics(index: number): void {
    const topic = this.topics.at(index);
    const current = topic.value.showSubtopics;
    topic.patchValue({ showSubtopics: !current });
  }

  getSubtopics(topicIndex: number): FormArray {
    return this.topics.at(topicIndex).get('subtopics') as FormArray;
  }


  addSubtopic(topicIndex: number): void {
    const subtopicGroup = this.fb.group({
      subtopic_title: ['', Validators.required],
      description: ['']
    });

    this.getSubtopics(topicIndex).push(subtopicGroup);
  }


  deleteSubtopic(topicIndex: number, subIndex: number): void {
    this.getSubtopics(topicIndex).removeAt(subIndex);
  }


  showAddUnit(): void {
    this.addUnitVisible = true;
    this.showAddAnotherBtn = false;
    this.initForm();
  }

  saveUnit(): void {
    this.markAllControlsTouched(this.unitForm);

    if (this.unitForm.invalid || this.topics.length === 0) {
      this.toastr.error('Please fill Unit Title, Duration and at least one topic.');
      return;
    }

    const formValue = this.unitForm.value;
    const hasValidTopic = formValue.topics.some(
      (t: any) =>
        t.title?.trim() &&
        (t.subtopics || []).some(
          (s: any) => s.subtopic_title?.trim()
        )
    );


    // if (!hasValidTopic) {
    //   this.toastr.error('Each topic must have a title and at least one subtopic.');
    //   return;
    // }

    const newUnit: Unit = {
      unit_title: formValue.unitTitle,
      unit_hours: Number(formValue.unitHours),
      topics: formValue.topics.map((t: any) => ({
        topic_title: t.title,
        description: t.description,

        subtopics: (t.subtopics || [])
          .filter(
            (s: any) =>
              s.subtopic_title?.trim() || s.description?.trim()
          )
          .map((s: any) => ({
            subtopic_title: s.subtopic_title?.trim(),
            description: s.description || ''
          }))
      }))
    };


    if (this.editingIndex !== null) {
      this.savedUnits[this.editingIndex] = newUnit;
      this.editingIndex = null;
      this.toastr.success('Unit updated successfully!');
    } else {
      this.savedUnits.push(newUnit);
      this.toastr.success('Unit saved successfully!');
    }

    this.courseService.updatePayload({ units: this.savedUnits });
    this.addUnitVisible = false;
    this.initForm();
  }


  editUnit(index: number): void {
    const unit = this.savedUnits[index];
    this.editingIndex = index;
    this.addUnitVisible = true;
    this.topics.clear();

    this.unitForm.patchValue({
      unitTitle: unit.unit_title,
      unitHours: unit.unit_hours
    });

    unit.topics.forEach((t) => {
      const topicGroup = this.fb.group({
        title: [t.topic_title, Validators.required],
        showSubtopics: [true],
        description: [t.description || ''],
        subtopics: this.fb.array([])
      });

      (t.subtopics || []).forEach((s) => {
        (topicGroup.get('subtopics') as FormArray).push(
          this.fb.group({
            subtopic_title: [s.subtopic_title, Validators.required],
            description: [s.description || '']
          })
        );
      });

      this.topics.push(topicGroup);
    });
  }


  get savedUnitsCount(): number {
    return this.savedUnits.length;
  }
  get isNextDisabled(): boolean {
    if (this.savedUnits.length === 0) {
      return true;
    }
    for (const unit of this.savedUnits) {
      if (!unit.unit_title || !unit.unit_hours || !unit.topics?.length) {
        return true;
      }

      for (const topic of unit.topics) {
        if (!topic.topic_title) {
          return true;
        }
      }
    }

    return false;
  }

  quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  goToPrePage(): void {
    this.router.navigate(['/admin-student-platform/course-setup/objectives']);
  }

  goToNextPage(): void {
    this.router.navigate(['/admin-student-platform/course-setup/outcomes']);

  }
}
